import React, { useState, useEffect, useCallback } from 'react';
import { scGet, scPost, scDelete } from '@/lib/scFetch';
import toast from 'react-hot-toast';

interface FileEntry {
  name: string;
  type: 'dir' | 'file';
  size: number;
  modified: number;
}

// ─── File Type Helpers ────────────────────────────────────────────────────────

const EXT_MAP: Record<string, { icon: string; color: string; label: string }> = {
  // Lua / FiveM scripting
  lua:  { icon: 'fa-solid fa-code',           color: 'text-sky-400',    label: 'Lua Script' },
  // Web / markup
  html: { icon: 'fa-brands fa-html5',         color: 'text-orange-400', label: 'HTML' },
  htm:  { icon: 'fa-brands fa-html5',         color: 'text-orange-400', label: 'HTML' },
  css:  { icon: 'fa-brands fa-css3-alt',      color: 'text-blue-400',   label: 'CSS' },
  // JavaScript / TypeScript
  js:   { icon: 'fa-brands fa-js',            color: 'text-yellow-400', label: 'JavaScript' },
  mjs:  { icon: 'fa-brands fa-js',            color: 'text-yellow-400', label: 'JS Module' },
  ts:   { icon: 'fa-solid fa-file-code',      color: 'text-blue-300',   label: 'TypeScript' },
  tsx:  { icon: 'fa-solid fa-file-code',      color: 'text-blue-300',   label: 'TSX' },
  jsx:  { icon: 'fa-solid fa-file-code',      color: 'text-yellow-300', label: 'JSX' },
  // Config / data
  json: { icon: 'fa-solid fa-braces',         color: 'text-yellow-400', label: 'JSON' },
  yaml: { icon: 'fa-solid fa-file-lines',     color: 'text-rose-400',   label: 'YAML' },
  yml:  { icon: 'fa-solid fa-file-lines',     color: 'text-rose-400',   label: 'YAML' },
  toml: { icon: 'fa-solid fa-file-lines',     color: 'text-orange-300', label: 'TOML' },
  cfg:  { icon: 'fa-solid fa-sliders',        color: 'text-emerald-400',label: 'Config' },
  ini:  { icon: 'fa-solid fa-gear',           color: 'text-slate-400',  label: 'INI Config' },
  env:  { icon: 'fa-solid fa-lock',           color: 'text-amber-400',  label: 'Env File' },
  xml:  { icon: 'fa-solid fa-code',           color: 'text-orange-400', label: 'XML' },
  // Images
  png:  { icon: 'fa-solid fa-image',          color: 'text-pink-400',   label: 'PNG Image' },
  jpg:  { icon: 'fa-solid fa-image',          color: 'text-pink-400',   label: 'JPEG Image' },
  jpeg: { icon: 'fa-solid fa-image',          color: 'text-pink-400',   label: 'JPEG Image' },
  gif:  { icon: 'fa-solid fa-film',           color: 'text-pink-400',   label: 'GIF' },
  webp: { icon: 'fa-solid fa-image',          color: 'text-pink-400',   label: 'WebP Image' },
  svg:  { icon: 'fa-solid fa-bezier-curve',   color: 'text-violet-400', label: 'SVG Vector' },
  ico:  { icon: 'fa-solid fa-image',          color: 'text-pink-300',   label: 'Icon' },
  // Audio
  mp3:  { icon: 'fa-solid fa-file-audio',     color: 'text-orange-400', label: 'MP3 Audio' },
  ogg:  { icon: 'fa-solid fa-file-audio',     color: 'text-orange-400', label: 'OGG Audio' },
  wav:  { icon: 'fa-solid fa-file-audio',     color: 'text-orange-400', label: 'WAV Audio' },
  flac: { icon: 'fa-solid fa-file-audio',     color: 'text-orange-300', label: 'FLAC Audio' },
  // Video
  mp4:  { icon: 'fa-solid fa-file-video',     color: 'text-red-400',    label: 'MP4 Video' },
  webm: { icon: 'fa-solid fa-file-video',     color: 'text-red-400',    label: 'WebM Video' },
  avi:  { icon: 'fa-solid fa-file-video',     color: 'text-red-400',    label: 'AVI Video' },
  // Archives
  zip:  { icon: 'fa-solid fa-file-zipper',    color: 'text-amber-400',  label: 'ZIP Archive' },
  rar:  { icon: 'fa-solid fa-file-zipper',    color: 'text-amber-400',  label: 'RAR Archive' },
  '7z': { icon: 'fa-solid fa-file-zipper',    color: 'text-amber-400',  label: '7-Zip Archive' },
  tar:  { icon: 'fa-solid fa-file-zipper',    color: 'text-amber-400',  label: 'TAR Archive' },
  gz:   { icon: 'fa-solid fa-file-zipper',    color: 'text-amber-400',  label: 'GZ Archive' },
  // Documents
  txt:  { icon: 'fa-solid fa-file-lines',     color: 'text-slate-400',  label: 'Text File' },
  md:   { icon: 'fa-brands fa-markdown',      color: 'text-violet-400', label: 'Markdown' },
  pdf:  { icon: 'fa-solid fa-file-pdf',       color: 'text-red-400',    label: 'PDF Document' },
  // Database
  sql:  { icon: 'fa-solid fa-database',       color: 'text-cyan-400',   label: 'SQL Script' },
  db:   { icon: 'fa-solid fa-database',       color: 'text-cyan-300',   label: 'Database' },
  // Shell / executables
  sh:   { icon: 'fa-solid fa-terminal',       color: 'text-green-400',  label: 'Shell Script' },
  bat:  { icon: 'fa-solid fa-terminal',       color: 'text-green-400',  label: 'Batch Script' },
  ps1:  { icon: 'fa-solid fa-terminal',       color: 'text-blue-400',   label: 'PowerShell' },
  exe:  { icon: 'fa-solid fa-microchip',      color: 'text-slate-400',  label: 'Executable' },
  dll:  { icon: 'fa-solid fa-puzzle-piece',   color: 'text-slate-400',  label: 'DLL Library' },
};

const IMAGE_EXTS = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'ico']);

function getExt(name: string): string {
  return name.split('.').pop()?.toLowerCase() ?? '';
}

function getFileIconClass(entry: FileEntry): string {
  if (entry.type === 'dir') return 'fa-solid fa-folder';
  return EXT_MAP[getExt(entry.name)]?.icon ?? 'fa-solid fa-file';
}

function getFileColor(entry: FileEntry): string {
  if (entry.type === 'dir') return 'text-amber-400';
  return EXT_MAP[getExt(entry.name)]?.color ?? 'text-slate-400';
}

function getFileLabel(entry: FileEntry): string {
  if (entry.type === 'dir') return 'Folder';
  return EXT_MAP[getExt(entry.name)]?.label ?? 'File';
}

function isEditable(name: string): boolean {
  const editExts = new Set(['lua','cfg','json','txt','md','html','htm','ini','xml','yaml','yml',
    'env','toml','css','js','ts','tsx','jsx','sh','bat','ps1','sql','mjs']);
  return editExts.has(getExt(name));
}

function isImage(name: string): boolean {
  return IMAGE_EXTS.has(getExt(name));
}

function formatSize(bytes: number): string {
  if (bytes === 0) return '—';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

// ─── Delete Confirmation Modal ────────────────────────────────────────────────

interface DeleteModalProps {
  entry: FileEntry;
  onConfirm: () => void;
  onCancel: () => void;
}

function DeleteModal({ entry, onConfirm, onCancel }: DeleteModalProps) {
  const isDir = entry.type === 'dir';
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={onCancel}
    >
      <div
        className="relative bg-[#0c0f18] border border-white/10 rounded-2xl p-7 w-full max-w-md shadow-2xl animate-fadeIn"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Red glow top accent */}
        <div className="absolute top-0 left-0 right-0 h-[1px] rounded-t-2xl bg-gradient-to-r from-transparent via-red-500/60 to-transparent" />

        <div className="flex flex-col items-center gap-4 text-center">
          {/* Warning icon */}
          <div className="w-14 h-14 rounded-full bg-red-500/10 border border-red-500/25 flex items-center justify-center">
            <i className={`fa-solid ${isDir ? 'fa-folder-minus' : 'fa-file-slash'} text-red-400 text-xl`} />
          </div>

          <div>
            <h3 className="text-base font-bold font-display text-white uppercase tracking-wider mb-1">
              Delete {isDir ? 'Folder' : 'File'}
            </h3>
            <p className="text-slate-400 text-sm leading-relaxed">
              Are you sure you want to permanently delete
            </p>
            <p className="mt-1.5 font-mono text-sm bg-white/[0.04] border border-white/[0.07] rounded-lg px-3 py-1.5 text-red-300 break-all">
              {isDir ? <i className="fa-solid fa-folder text-amber-400 mr-1.5 text-xs" /> : null}
              {entry.name}
            </p>
            {isDir && (
              <p className="mt-2 text-[11px] text-red-400/80">
                <i className="fa-solid fa-triangle-exclamation mr-1" />
                All contents inside this folder will be deleted.
              </p>
            )}
          </div>

          <div className="flex gap-3 w-full mt-1">
            <button
              onClick={onCancel}
              className="flex-1 py-2.5 rounded-xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.07] text-slate-300 text-xs font-display font-bold uppercase tracking-wider transition-all"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              className="flex-1 py-2.5 rounded-xl bg-red-500/20 border border-red-500/40 hover:bg-red-500/30 hover:border-red-400/60 text-red-300 hover:text-red-100 text-xs font-display font-bold uppercase tracking-wider transition-all hover:shadow-[0_0_20px_rgba(239,68,68,0.3)]"
            >
              <i className="fa-solid fa-trash-can mr-1.5" />
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Image Preview Modal ──────────────────────────────────────────────────────

interface ImagePreviewProps {
  name: string;
  src: string;
  onClose: () => void;
}

function ImagePreview({ name, src, onClose }: ImagePreviewProps) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md"
      onClick={onClose}
    >
      <div
        className="relative flex flex-col bg-[#0c0f18] border border-white/10 rounded-2xl overflow-hidden shadow-2xl max-w-3xl w-full mx-4 animate-fadeIn"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-pink-400/50 to-transparent" />
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-white/[0.05]">
          <span className="text-xs font-mono text-slate-300 flex items-center gap-2">
            <i className="fa-solid fa-image text-pink-400" />
            {name}
          </span>
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-white transition-colors p-1"
          >
            <i className="fa-solid fa-xmark" />
          </button>
        </div>
        {/* Image */}
        <div className="flex items-center justify-center p-6 bg-[#070a12]">
          <img
            src={src}
            alt={name}
            className="max-h-[60vh] max-w-full rounded-lg object-contain shadow-lg"
            style={{ imageRendering: 'auto' }}
          />
        </div>
        {/* Footer */}
        <div className="px-5 py-3 border-t border-white/[0.05] flex justify-end">
          <button
            onClick={onClose}
            className="text-xs font-mono text-slate-500 hover:text-slate-300 transition-colors"
          >
            <i className="fa-solid fa-xmark mr-1" /> Close preview
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function FilesPage() {
  const [currentPath, setCurrentPath] = useState<string[]>([]);
  const [entries, setEntries] = useState<FileEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [showMkdir, setShowMkdir] = useState(false);
  const [sftp, setSftp] = useState<{ host: string; port: number; username: string } | null>(null);
  const [launchingWinScp, setLaunchingWinScp] = useState(false);

  // Delete confirmation modal
  const [deleteTarget, setDeleteTarget] = useState<FileEntry | null>(null);

  // Image preview modal
  const [imagePreview, setImagePreview] = useState<{ name: string; src: string } | null>(null);

  // File editor
  const [editingFile, setEditingFile] = useState<string | null>(null);
  const [editorContent, setEditorContent] = useState('');
  const [savingFile, setSavingFile] = useState(false);

  const getPathString = (subPath = currentPath) => subPath.join('/');

  const fetchFiles = useCallback(async (pathArray = currentPath) => {
    setLoading(true);
    try {
      const data = await scGet(`/fivem/files?path=${encodeURIComponent(getPathString(pathArray))}`);
      setEntries(data.entries || []);
    } catch (err: any) {
      toast.error('Failed to load files: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [currentPath]);

  useEffect(() => { fetchFiles(); }, [currentPath]);

  const handleNavigate = (dirName: string) => {
    setEditingFile(null);
    setCurrentPath([...currentPath, dirName]);
  };

  const handleGoBack = () => {
    setEditingFile(null);
    if (currentPath.length > 0) setCurrentPath(currentPath.slice(0, -1));
  };

  const handleBreadcrumb = (index: number) => {
    setEditingFile(null);
    setCurrentPath(currentPath.slice(0, index + 1));
  };

  const handleGoRoot = () => {
    setEditingFile(null);
    setCurrentPath([]);
  };

  // ── Create folder ──
  const handleMkdir = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newFolderName.trim()) return;
    try {
      await scPost('/fivem/files/mkdir', { path: getPathString([...currentPath, newFolderName.trim()]) });
      toast.success(`Folder "${newFolderName}" created`);
      setNewFolderName('');
      setShowMkdir(false);
      fetchFiles();
    } catch (err: any) {
      toast.error('Failed to create folder: ' + err.message);
    }
  };

  // ── Upload ──
  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('path', getPathString());
    formData.append('file', file);
    const token = sessionStorage.getItem('fivem_token') || '';
    const toastId = toast.loading('Uploading file...');
    try {
      const res = await fetch('/fivem/files/upload', {
        method: 'POST',
        headers: { 'X-FiveM-Token': token },
        body: formData,
      });
      if (!res.ok) throw new Error(await res.text());
      toast.success(`"${file.name}" uploaded`, { id: toastId });
      fetchFiles();
    } catch (err: any) {
      toast.error('Upload failed: ' + err.message, { id: toastId });
    }
  };

  // ── Delete (two-step) ──
  const requestDelete = (entry: FileEntry) => setDeleteTarget(entry);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const entry = deleteTarget;
    setDeleteTarget(null);
    try {
      await scPost('/fivem/files/delete', { path: getPathString([...currentPath, entry.name]) });
      toast.success(`Deleted ${entry.name}`);
      if (editingFile === entry.name) setEditingFile(null);
      fetchFiles();
    } catch (err: any) {
      toast.error('Delete failed: ' + err.message);
    }
  };

  // ── Download ──
  const handleDownload = async (entry: FileEntry) => {
    const pathStr = getPathString([...currentPath, entry.name]);
    const token = sessionStorage.getItem('fivem_token') || '';
    try {
      const res = await fetch(`/fivem/files/download?path=${encodeURIComponent(pathStr)}`, {
        headers: { 'X-FiveM-Token': token },
      });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = entry.name;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${entry.name}`);
    } catch (err: any) {
      toast.error('Download failed: ' + err.message);
    }
  };

  // ── Image preview ──
  const handleImagePreview = async (entry: FileEntry) => {
    const pathStr = getPathString([...currentPath, entry.name]);
    const token = sessionStorage.getItem('fivem_token') || '';
    try {
      const res = await fetch(`/fivem/files/download?path=${encodeURIComponent(pathStr)}`, {
        headers: { 'X-FiveM-Token': token },
      });
      if (!res.ok) throw new Error('Could not load image');
      const blob = await res.blob();
      const src = window.URL.createObjectURL(blob);
      setImagePreview({ name: entry.name, src });
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  // ── File editor ──
  const handleEdit = async (entry: FileEntry) => {
    const pathStr = getPathString([...currentPath, entry.name]);
    const token = sessionStorage.getItem('fivem_token') || '';
    setLoading(true);
    try {
      const res = await fetch(`/fivem/files/download?path=${encodeURIComponent(pathStr)}`, {
        headers: { 'X-FiveM-Token': token },
      });
      if (!res.ok) throw new Error('Failed to retrieve file contents.');
      setEditingFile(entry.name);
      setEditorContent(await res.text());
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editingFile) return;
    setSavingFile(true);
    try {
      await scPost('/fivem/files/edit', {
        path: getPathString([...currentPath, editingFile]),
        content: editorContent,
      });
      toast.success(`Saved "${editingFile}"`);
      setEditingFile(null);
      fetchFiles();
    } catch (err: any) {
      toast.error('Save failed: ' + err.message);
    } finally {
      setSavingFile(false);
    }
  };

  // ── SFTP / WinSCP ──
  const handleOpenWinScp = async () => {
    setLaunchingWinScp(true);
    try {
      const data = await scGet('/fivem/sftp/info');
      setSftp(data);
      window.location.href = `winscp://${encodeURIComponent(data.username)}@${data.host}:${data.port}/`;
      toast.success(`WinSCP launching — ${data.username}@${data.host}:${data.port}`, { duration: 5000 });
    } catch (err: any) {
      toast.error('Could not load SFTP info: ' + err.message);
    } finally {
      setLaunchingWinScp(false);
    }
  };

  const handleCopySftp = async () => {
    let info = sftp;
    if (!info) {
      try {
        info = await scGet('/fivem/sftp/info');
        setSftp(info);
      } catch (err: any) {
        toast.error('Could not load SFTP info: ' + err.message);
        return;
      }
    }
    await navigator.clipboard.writeText(`sftp://${info!.username}@${info!.host}:${info!.port}/`);
    toast.success('SFTP connection string copied!');
  };

  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col gap-6">
      {/* Modals */}
      {deleteTarget && (
        <DeleteModal
          entry={deleteTarget}
          onConfirm={confirmDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
      {imagePreview && (
        <ImagePreview
          name={imagePreview.name}
          src={imagePreview.src}
          onClose={() => {
            window.URL.revokeObjectURL(imagePreview.src);
            setImagePreview(null);
          }}
        />
      )}

      {/* Header */}
      <div className="flex justify-between items-center flex-wrap gap-4">
        <div>
          <h2 className="text-xl font-bold font-display uppercase tracking-wider text-white">File Manager</h2>
          <p className="text-xs text-slate-400">Navigate server assets, upload configurations, and edit scripts.</p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* WinSCP */}
          <button
            id="btn-open-winscp"
            onClick={handleOpenWinScp}
            disabled={launchingWinScp}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-violet-500/30 bg-violet-500/10 hover:bg-violet-500/20 hover:border-violet-400/50 text-violet-300 hover:text-violet-100 font-display font-bold uppercase tracking-wider text-[10px] transition-all duration-200 hover:shadow-[0_0_16px_rgba(139,92,246,0.35)] disabled:opacity-50 disabled:cursor-not-allowed"
            title="Open SFTP session in WinSCP"
          >
            <i className={`fa-solid ${launchingWinScp ? 'fa-spinner animate-spin' : 'fa-network-wired'}`} />
            Open in WinSCP
          </button>

          {/* Copy SFTP */}
          <button
            id="btn-copy-sftp"
            onClick={handleCopySftp}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-white/[0.08] bg-white/[0.03] hover:bg-white/[0.06] text-slate-400 hover:text-slate-200 font-display font-bold uppercase tracking-wider text-[10px] transition-all duration-200"
            title="Copy SFTP connection string"
          >
            <i className="fa-solid fa-copy" />
            Copy SFTP
          </button>

          {/* New Folder */}
          <button className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] text-xs font-semibold text-slate-300 hover:text-white transition-all" onClick={() => setShowMkdir(!showMkdir)}>
            <i className="fa-solid fa-folder-plus mr-1" /> New Folder
          </button>

          {/* Upload */}
          <label className="px-4 py-2 bg-[#00ffd1] hover:bg-[#00ffd1]/90 hover:shadow-cyanGlow text-slate-950 font-display font-bold uppercase tracking-wider text-[10px] rounded-xl cursor-pointer transition-all inline-flex items-center gap-1.5">
            <i className="fa-solid fa-upload" /> Upload File
            <input type="file" onChange={handleUpload} className="hidden" />
          </label>
        </div>
      </div>

      {/* SFTP info bar */}
      {sftp && (
        <div className="flex items-center gap-3 bg-violet-500/[0.06] border border-violet-500/20 rounded-xl px-5 py-3 text-xs font-mono">
          <i className="fa-solid fa-plug-circle-check text-violet-400 text-sm" />
          <span className="text-slate-400">SFTP:</span>
          <span className="text-violet-300 font-semibold">
            sftp://<span className="text-white">{sftp.username}</span>@{sftp.host}:<span className="text-[#00ffd1]">{sftp.port}</span>/
          </span>
          <span className="ml-auto text-[9px] text-slate-600 uppercase tracking-widest">WinSCP session tracked</span>
        </div>
      )}

      {/* New folder form */}
      {showMkdir && (
        <div className="glass-card">
          <form onSubmit={handleMkdir} className="flex gap-3">
            <input
              type="text"
              placeholder="Enter folder name..."
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              className="flex-1 bg-white/[0.02] border border-white/10 rounded-xl px-4 py-2 text-slate-100 text-xs font-mono focus:outline-none focus:border-[#00ffd1]/50"
              autoFocus
            />
            <button type="submit" className="px-4 py-2 rounded-xl bg-[#00ffd1] text-slate-950 hover:bg-[#00ffd1]/90 font-semibold text-xs transition-all">Create</button>
            <button className="px-3 py-1.5 rounded-xl bg-transparent hover:bg-white/[0.03] text-slate-400 hover:text-slate-200 text-xs font-semibold transition-all" onClick={() => setShowMkdir(false)}>Cancel</button>
          </form>
        </div>
      )}

      {/* Breadcrumbs */}
      <div className="flex items-center gap-1.5 text-xs font-mono text-slate-500 bg-white/[0.01] border border-white/[0.04] p-3 rounded-xl overflow-x-auto">
        <button onClick={handleGoRoot} className="hover:text-[#00ffd1] transition-colors whitespace-nowrap">
          <i className="fa-solid fa-hdd mr-1 text-[11px]" /> root
        </button>
        {currentPath.map((folder, index) => (
          <React.Fragment key={index}>
            <span className="text-slate-700">/</span>
            <button onClick={() => handleBreadcrumb(index)} className="hover:text-[#00ffd1] transition-colors text-slate-300 whitespace-nowrap">
              {folder}
            </button>
          </React.Fragment>
        ))}
      </div>

      {/* Two-pane layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 min-h-[500px]">
        {/* File list */}
        <div className={editingFile ? 'lg:col-span-6' : 'lg:col-span-12'}>
          <div className="glass-card">
            <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-cyberCyan/10 to-transparent" />
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs font-mono">
                <thead className="bg-white/[0.02] text-slate-400 uppercase tracking-wider border-b border-white/[0.04]">
                  <tr>
                    <th className="px-5 py-3.5">Name</th>
                    <th className="px-5 py-3.5 hidden sm:table-cell">Type</th>
                    <th className="px-5 py-3.5 hidden sm:table-cell">Size</th>
                    <th className="px-5 py-3.5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.04] text-slate-200">
                  {/* Parent dir row */}
                  {currentPath.length > 0 && (
                    <tr className="hover:bg-white/[0.01] transition-colors">
                      <td colSpan={4} className="px-5 py-3.5">
                        <button onClick={handleGoBack} className="text-[#00ffd1] hover:underline flex items-center gap-2">
                          <i className="fa-solid fa-arrow-turn-up text-[10px]" /> .. (parent directory)
                        </button>
                      </td>
                    </tr>
                  )}

                  {entries.length === 0 && !loading && (
                    <tr>
                      <td colSpan={4} className="px-5 py-8 text-center text-slate-500">
                        This directory is empty.
                      </td>
                    </tr>
                  )}

                  {entries.map((entry) => {
                    const iconClass = getFileIconClass(entry);
                    const color     = getFileColor(entry);
                    const label     = getFileLabel(entry);
                    const canEdit   = entry.type === 'file' && isEditable(entry.name);
                    const canView   = entry.type === 'file' && isImage(entry.name);

                    return (
                      <tr key={entry.name} className="hover:bg-white/[0.015] transition-colors group">
                        {/* Name + icon */}
                        <td className="px-5 py-3">
                          {entry.type === 'dir' ? (
                            <button
                              onClick={() => handleNavigate(entry.name)}
                              className="font-semibold text-slate-300 hover:text-[#00ffd1] hover:underline flex items-center gap-2.5"
                            >
                              <i className={`${iconClass} ${color} text-sm w-4 text-center`} />
                              {entry.name}
                            </button>
                          ) : canView ? (
                            <button
                              onClick={() => handleImagePreview(entry)}
                              className="flex items-center gap-2.5 text-slate-400 hover:text-pink-300 transition-colors"
                            >
                              <i className={`${iconClass} ${color} text-sm w-4 text-center`} />
                              <span>{entry.name}</span>
                              <i className="fa-solid fa-eye text-[10px] text-pink-400/60 opacity-0 group-hover:opacity-100 transition-opacity" />
                            </button>
                          ) : (
                            <div className="flex items-center gap-2.5 text-slate-400">
                              <i className={`${iconClass} ${color} text-sm w-4 text-center`} />
                              <span>{entry.name}</span>
                            </div>
                          )}
                        </td>

                        {/* Type badge */}
                        <td className="px-5 py-3 hidden sm:table-cell">
                          <span className="text-[10px] font-mono text-slate-600">{label}</span>
                        </td>

                        {/* Size */}
                        <td className="px-5 py-3 text-slate-500 hidden sm:table-cell">
                          {formatSize(entry.size)}
                        </td>

                        {/* Actions */}
                        <td className="px-5 py-3 text-right">
                          <div className="flex justify-end gap-1.5 items-center">
                            {/* Image preview */}
                            {canView && (
                              <button
                                onClick={() => handleImagePreview(entry)}
                                className="text-slate-500 hover:text-pink-400 transition-colors p-1.5 rounded-lg hover:bg-pink-400/10"
                                title="Preview image"
                              >
                                <i className="fa-solid fa-eye text-[11px]" />
                              </button>
                            )}
                            {/* Download */}
                            {entry.type === 'file' && (
                              <button
                                onClick={() => handleDownload(entry)}
                                className="text-slate-500 hover:text-[#00ffd1] transition-colors p-1.5 rounded-lg hover:bg-[#00ffd1]/10"
                                title="Download"
                              >
                                <i className="fa-solid fa-download text-[11px]" />
                              </button>
                            )}
                            {/* Edit */}
                            {canEdit && (
                              <button
                                onClick={() => handleEdit(entry)}
                                className="text-slate-500 hover:text-[#00ffd1] transition-colors p-1.5 rounded-lg hover:bg-[#00ffd1]/10"
                                title="Edit file"
                              >
                                <i className="fa-solid fa-pen-to-square text-[11px]" />
                              </button>
                            )}
                            {/* Delete */}
                            <button
                              onClick={() => requestDelete(entry)}
                              className="text-slate-500 hover:text-red-400 transition-colors p-1.5 rounded-lg hover:bg-red-400/10"
                              title="Delete"
                            >
                              <i className="fa-solid fa-trash-can text-[11px]" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}

                  {loading && (
                    <tr>
                      <td colSpan={4} className="px-5 py-8 text-center text-[#00ffd1] font-bold">
                        <i className="fa-solid fa-spinner animate-spin mr-2" /> Syncing file system...
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Inline Editor Pane */}
        {editingFile && (
          <div className="lg:col-span-6 flex flex-col h-full">
            <div className="glass-card">
              <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-cyberCyan/10 to-transparent" />
              <div className="flex justify-between items-center mb-4">
                <span className="text-xs font-bold text-white font-mono flex items-center gap-2">
                  <i className={`${getFileIconClass({ name: editingFile, type: 'file', size: 0, modified: 0 })} ${getFileColor({ name: editingFile, type: 'file', size: 0, modified: 0 })} text-sm`} />
                  {editingFile}
                </span>
                <div className="flex gap-2">
                  <button className="px-4 py-2 rounded-xl bg-[#00ffd1] text-slate-950 hover:bg-[#00ffd1]/90 font-semibold text-xs transition-all" onClick={handleSaveEdit} loading={savingFile}>Save</button>
                  <button className="px-3 py-1.5 rounded-xl bg-transparent hover:bg-white/[0.03] text-slate-400 hover:text-slate-200 text-xs font-semibold transition-all" onClick={() => setEditingFile(null)}>Cancel</button>
                </div>
              </div>
              <textarea
                value={editorContent}
                onChange={(e) => setEditorContent(e.target.value)}
                className="flex-1 w-full bg-[#030508] border border-white/5 rounded-xl p-4 text-slate-300 font-mono text-xs focus:outline-none focus:border-[#00ffd1]/20 resize-none h-[420px]"
                spellCheck={false}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default FilesPage;
